<template>
    <div>
        <AppConfirmDeleteDialog
            :id="idToDelete"
            title="Delete License"
            :text="`Are you sure you want to delete license with Organization Name = '${itemName}'`"
            @submit-delete="handleConfirmDelete"
        />

        <AppSection v-if="$slots.hasOwnProperty('searchInputs')">
            <el-row :gutter="10">
                <slot name="searchInputs" />
                <el-col v-if="showSearch" :sm="2" class="text-right">
                    <el-button type="primary" @click="handleSearch">
                        SEARCH
                    </el-button>
                </el-col>
            </el-row>
        </AppSection>

        <AppSection class="pa-0">
            <el-table
                ref="tableRef"
                v-loading="loading"
                :data="paginationData.data"
                :highlight-current-row="true"
                :automatic-dropdown="true"
                @sort-change="handleSortChange"
            >
                <template v-for="item in headers" :key="item.value">
                    <el-table-column
                        :prop="item.value"
                        :label="item.text"
                        :sortable="item.sortable ? 'custom' : false"
                    >
                    </el-table-column>
                </template>
                <el-table-column
                    v-if="action"
                    label="Actions"
                    align="right"
                    width="200"
                >
                    <template #default="scope">
                        <el-button
                            class="clickable"
                            size="small"
                            :icon="Edit"
                            @click="handleClickEdit(scope.$index)"
                            >Edit</el-button
                        >
                        <el-button
                            class="clickable"
                            size="small"
                            type="danger"
                            :icon="Delete"
                            @click="
                                handleClickDelete(
                                    scope.row.id,
                                    scope.row.organizationName,
                                )
                            "
                            >Delete</el-button
                        >
                    </template>
                </el-table-column>
            </el-table>
            <el-pagination
                class="margin-20-20"
                :current-page="paginationData.currentPage"
                :pager-count="5"
                :page-sizes="[1, 2, 5, 10, 20, 30]"
                :page-size="paginationData.pageSize"
                layout="total, sizes, prev, pager, next, jumper"
                :total="paginationData.found"
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
            >
            </el-pagination>
        </AppSection>
    </div>
</template>

<script lang="ts">
import AppConfirmDeleteDialog from '@/components/AppConfirmDeleteDialog.vue';
import { License } from '@/types/License';
import {
    DEFAULT_LIMIT,
    PaginationData,
    QueryPaginate,
    SortEvent,
} from '@/types/PaginationData';
import { defineComponent, PropType, reactive, toRefs } from 'vue';
import AppSection from '@/components/AppSection.vue';
import { Edit, Delete } from '@element-plus/icons';

export interface DataTableHeader {
    text: string;
    sortable?: boolean;
    value: string;
    selected: boolean;
}

const SearchArea = defineComponent({
    components: {
        AppSection,
        AppConfirmDeleteDialog,
    },
    props: {
        headers: {
            type: Array as PropType<DataTableHeader[]>,
            required: true,
        },
        query: {
            type: Object,
            required: true,
        },
        paginationData: {
            type: Object as PropType<PaginationData<License>>,
            required: true,
        },
        loading: {
            type: Boolean,
        },
        action: {
            type: Boolean,
            default: () => true,
        },
        showSearch: {
            type: Boolean,
            default: () => false,
        },
    },
    emits: [
        'edit',
        'delete',
        'search',
        'size-change',
        'current-change',
        'sort',
    ],
    setup(props, context) {
        const data = reactive({
            idToDelete: null as number | null,
            itemName: '',
        });

        const handleClickEdit = (index: number) => {
            context.emit('edit', index);
        };

        const handleClickDelete = (id: number, name: string) => {
            data.idToDelete = id;
            data.itemName = name;
        };

        const handleConfirmDelete = (isAgree: boolean) => {
            console.log(isAgree);
            if (isAgree) {
                context.emit('delete', data.idToDelete);
            }
            data.idToDelete = null;
        };

        const handleSearch = () => {
            context.emit('search');
        };

        const handleSizeChange = (val: number) => {
            context.emit('size-change', val);
        };

        const handleCurrentChange = (val: number) => {
            const limit =
                props.paginationData.pageSize > 0
                    ? props.paginationData.pageSize
                    : DEFAULT_LIMIT;
            const event = {
                limit: limit,
                offset: (val - 1) * props.paginationData.pageSize,
            } as QueryPaginate;
            context.emit('current-change', event);
        };

        const handleSortChange = (event: any) => {
            const sort = {
                sortBy: [event.prop ? event.prop : ''],
                sortDesc: [
                    !event.order
                        ? ''
                        : event.order == 'ascending'
                          ? false
                          : true,
                ],
            } as SortEvent;
            context.emit('sort', sort);
        };

        return {
            ...toRefs(data),
            handleClickEdit,
            handleClickDelete,
            handleConfirmDelete,
            handleSearch,
            handleSizeChange,
            handleCurrentChange,
            handleSortChange,
            Edit,
            Delete,
        };
    },
});
export default SearchArea;
</script>
